import React, { useMemo } from 'react';

import styled from 'styled-components';

import { isLongEvent } from '@libs/EventDetails';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadFollowup } from '@hooks/API/Event/UseFollowup';

import HorizontalPeriod from '@components/Dates/HorizontalPeriod';
import AcknowledgementEvent from '@components/EventButtons/AcknowledgementEvent';
import CancelEvent from '@components/EventButtons/CancelEvent';
import { useFollowUp } from '@components/EventDrawer/FollowUpEvent/FollowUpContext';
import { NwButtonGroup } from '@components/Gui/NwButton';

// Helper to extract time portion (HH:mm) from a date string
const getTimeFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${date.getHours()}:${date.getMinutes()}`;
};

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem 0.75rem 0.5rem;
    border-bottom: ${(props) => props.theme.DefaultBorder};

    .left-content {
        text-align: left;
        width: 10rem;
    }
    .middle-content {
        flex: 1;
        text-align: left;
        padding-left: 0.5rem;
        h1 {
            margin: 0;
            font-size: ${(props) => props.theme.FontSizeLg};
        }
    }
    .right-content {
        text-align: center;
        width: 8rem;

        .nw-dropdown-menu-button-group {
            justify-content: center;
        }
    }
    &.is-cancelled {
        h1 {
            ${(props) => props.theme.RedLineThrough(1)}
        }
    }
`;

const ButtonGroupWrapper = styled.div`
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-right: 1px solid #d9d9d9 !important;
    }
    .ant-btn:nth-child(2),
    span:nth-child(2) > .ant-btn,
    a:nth-child(2) > .ant-btn {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
    }
`;

export const FollowUpModelNameAndDate = () => {
    const { groupType, followUpData, afterUpdate, followUpID, periodFirstDate, periodLastDate } = useFollowUp();

    // Check if times differ between root event and related events (for 'models' groupType)
    const hasDifferentTimes = useMemo(() => {
        if (groupType !== 'models' || !followUpData?.RelatedEvents?.length) {
            return false;
        }

        const rootStartTime = getTimeFromDate(followUpData.StartDate);
        const rootEndTime = getTimeFromDate(followUpData.EndDate);

        return followUpData.RelatedEvents.some((event) => {
            const eventStartTime = getTimeFromDate(event.StartDate);
            const eventEndTime = getTimeFromDate(event.EndDate);
            return eventStartTime !== rootStartTime || eventEndTime !== rootEndTime;
        });
    }, [groupType, followUpData]);

    const actions = {
        onAcknowledgmentUpdate: () => {
            forceReloadFollowup(followUpData.ID);
            afterUpdate();
        },
        onCancelUpdate: () => {
            forceReloadFollowup(followUpData.ID);
            afterUpdate();
        },
    };

    return (
        <Wrapper $isCancelled={followUpData.Cancel}>
            <div className="left-content">
                {groupType === 'period' ? (
                    <HorizontalPeriod startDate={periodFirstDate} endDate={periodLastDate} size="small" time={false} />
                ) : (
                    <HorizontalPeriod
                        startDate={followUpData.StartDate}
                        endDate={followUpData.EndDate}
                        size={isLongEvent(followUpData.StartDate, followUpData.EndDate) ? 'small' : null}
                        time={!hasDifferentTimes}
                    />
                )}
            </div>
            <div className="middle-content">
                {groupType === 'models' ? (
                    <h1>{followUpData.RelatedEvents.length + 1} models</h1>
                ) : (
                    <h1>{getContactName(followUpData.ModelID)}</h1>
                )}
            </div>
            <div className="right-content">
                {!groupType && (actions.onAcknowledgmentUpdate || actions.onCancelUpdate) && (
                    <ButtonGroupWrapper>
                        <NwButtonGroup right>
                            <AcknowledgementEvent
                                button
                                onStatusChanged={actions.onAcknowledgmentUpdate}
                                acknowledgmentValue={followUpData.Acknowledgment}
                                type="FollowUp"
                                eventId={followUpID}
                            />
                            <CancelEvent
                                button
                                onStatusChanged={actions.onCancelUpdate}
                                cancelValue={followUpData.Cancel}
                                type="FollowUp"
                                eventId={followUpID}
                            />
                        </NwButtonGroup>
                    </ButtonGroupWrapper>
                )}
            </div>
        </Wrapper>
    );
};
